import axios from 'axios'
import { formatYearRange, toNullableNumber } from '../../utils/media.js'
import { normalizeYouTubeTrailer } from '../../utils/kinobdMedia.js'
import { vidriftPlayer } from './vidriftPlayback.js'

export const CINEMETA_API = String(
    process.env.VUE_APP_CINEMETA_API_BASE || 'https://v3-cinemeta.strem.io'
).replace(/\/$/, '')
export const VIDRIFT_METADATA_TIMEOUT = 10000
const CACHE_TTL = 5 * 60 * 1000
const TYPES = ['movie', 'series']

function imdbId(value) {
    return /^tt\d+$/.test(String(value || '')) ? String(value) : null
}

function tmdbId(value) {
    const id = Number(value)
    return Number.isSafeInteger(id) && id > 0 ? id : null
}

function imageUrl(value) {
    return /^https:\/\//.test(String(value || '')) ? value : null
}

export function normalizeCinemetaEpisodes(videos) {
    const seen = new Set()
    return (Array.isArray(videos) ? videos : []).flatMap((video) => {
        const season = Number(video?.season)
        const episode = Number(video?.episode ?? video?.number)
        if (!Number.isSafeInteger(season) || season < 1 || !Number.isSafeInteger(episode) || episode < 1) return []
        const key = `${season}:${episode}`
        if (seen.has(key)) return []
        seen.add(key)
        const releasedAt = Date.parse(video.released || video.firstAired || '')
        return [{
            season, episode, title: typeof video.name === 'string' ? video.name : '',
            releasedAt: Number.isFinite(releasedAt) ? releasedAt : null,
        }]
    }).sort((first, second) => first.season - second.season || first.episode - second.episode)
}

export function normalizeCinemeta(meta, kind, details = false) {
    const imdb = imdbId(meta && (meta.imdb_id || meta.id))
    if (!meta || !imdb || !TYPES.includes(kind) || (meta.type && meta.type !== kind)) return null
    const years = String(meta.releaseInfo || meta.year || '').match(/\d{4}/g) || []
    const year = toNullableNumber(years[0])
    const yearEnd = toNullableNumber(years[1])
    const poster = imageUrl(meta.poster)
    const backdrop = imageUrl(meta.background)
    const trailers = (Array.isArray(meta.trailers) ? meta.trailers : [])
        .map((trailer) => normalizeYouTubeTrailer({ provider: 'youtube', id: trailer.source }))
        .filter(Boolean)
    const strings = (value) => (Array.isArray(value) ? value : []).filter((entry) => typeof entry === 'string')
    return {
        key: `vidrift:${kind}:${imdb}`, provider: 'vidrift', id: `${kind}:${imdb}`, kind,
        title: meta.name || 'Без названия', originalTitle: meta.name || '', year, yearEnd,
        poster, backdrop: backdrop || poster,
        backdropCandidates: [backdrop, poster].filter(Boolean),
        subtitle: [kind === 'series' ? 'Сериал' : 'Фильм', formatYearRange(year, yearEnd)].filter(Boolean).join(' · '),
        ids: { kinopoisk: null, imdb, tmdb: tmdbId(meta.moviedb_id), shikimori: null },
        isEpisodic: kind === 'series', releaseType: kind === 'series' ? 'serial' : 'film',
        releaseStatus: kind === 'movie' || meta.status === 'Ended' ? 'released' : 'unknown',
        description: meta.description || '',
        durationMinutes: toNullableNumber(String(meta.runtime || '').match(/^\d+/)?.[0]),
        premiere: String(meta.released || '').slice(0, 10),
        genres: strings(meta.genres || meta.genre), countries: String(meta.country || '').split(', ').filter(Boolean),
        directors: strings(meta.director), actors: strings(meta.cast),
        ratings: { kinopoisk: null, imdb: toNullableNumber(meta.imdbRating) },
        images: { posters: poster ? [{ url: poster }] : [], backdrops: backdrop ? [{ url: backdrop }] : [], stills: [] },
        trailer: trailers[0] || null, trailerCandidates: trailers,
        detailsHydrated: details,
        episodeOptions: kind === 'series' && details ? normalizeCinemetaEpisodes(meta.videos) : [],
    }
}

function withSnapshot(media, snapshot) {
    if (!snapshot) return media
    // Keep stable identities and existing localized library text during migration.
    const localized = snapshot.provider !== 'vidrift'
    return {
        ...media,
        ...(localized ? {
            title: snapshot.title || media.title,
            description: snapshot.description || media.description,
            genres: snapshot.genres?.length ? snapshot.genres : media.genres,
        } : {}),
        ids: { ...snapshot.ids, ...Object.fromEntries(Object.entries(media.ids).filter(([, id]) => id != null)) },
    }
}

export function createVidriftProvider(client = axios) {
    const cache = new Map()
    async function request(path, signal) {
        if (signal?.aborted) { const error = new Error('Aborted'); error.name = 'AbortError'; throw error }
        const cached = cache.get(path)
        if (cached && Date.now() - cached.at < CACHE_TTL) return cached.data
        const response = await client.get(`${CINEMETA_API}${path}`, { signal, timeout: VIDRIFT_METADATA_TIMEOUT })
        const data = response.data
        if (!data || typeof data !== 'object'
            || path.startsWith('/catalog/') && !Array.isArray(data.metas)
            || path.startsWith('/meta/') && !Object.prototype.hasOwnProperty.call(data, 'meta')) {
            throw new Error('Invalid Cinemeta response')
        }
        cache.set(path, { at: Date.now(), data })
        if (cache.size > 100) cache.delete(cache.keys().next().value)
        return data
    }
    async function catalogs(extra, signal) {
        const results = await Promise.allSettled(TYPES.map(async (kind) => {
            const data = await request(`/catalog/${kind}/top${extra}.json`, signal)
            if (!Array.isArray(data.metas)) throw new Error('Invalid Cinemeta catalog')
            return data.metas.map((meta, rank) => ({ meta, rank, media: normalizeCinemeta(meta, kind) })).filter(({ media }) => media)
        }))
        if (signal?.aborted || results.every((result) => result.status === 'rejected')) {
            throw results.find((result) => result.status === 'rejected')?.reason || new Error('Aborted')
        }
        return results.filter((result) => result.status === 'fulfilled').flatMap((result) => result.value)
    }
    const provider = {
        id: 'vidrift',
        capabilities: Object.freeze({
            anime: false, movies: true, series: true, search: true, details: true,
            iframe: true, playerById: false, embeddedEpisodeSelector: true,
            embeddedVoiceSelector: true, progress: true, resume: true,
            structuredEpisodes: true,
        }),
        async search(query, { signal } = {}) {
            const text = String(query || '').trim()
            if (text.length < 2) return []
            // Keep the first series hits visible even when movies fill the
            // compact search result limit (and vice versa).
            return (await catalogs(`/search=${encodeURIComponent(text)}`, signal))
                .sort((first, second) => first.rank - second.rank)
                .map(({ media }) => media)
        },
        async discoverTop({ signal, page = 1, limit = 100 } = {}) {
            const skip = (Math.max(1, Math.floor(Number(page) || 1)) - 1) * 100
            return (await catalogs(skip ? `/skip=${skip}` : '', signal)).slice(0, limit).map(({ meta, media }, index) => ({
                source: 'cinemeta-top', media,
                signals: {
                    ratings: media.ratings, votes: { kinopoisk: null, imdb: null, shikimori: null },
                    popularity: toNullableNumber(meta.popularity), premiereAt: media.premiere || null,
                    updatedAt: null, status: media.releaseStatus, playbackQuality: null, sourceRank: index,
                },
            }))
        },
        async getDetails(id, { signal } = {}) {
            const match = /^(movie|series):(tt\d+)$/.exec(String(id || ''))
            if (!match) return null
            try {
                const data = await request(`/meta/${match[1]}/${match[2]}.json`, signal)
                const media = normalizeCinemeta(data.meta, match[1], true)
                return media?.ids.imdb === match[2] ? media : null
            } catch (error) {
                if (error.response?.status === 404) return null
                throw error
            }
        },
        async resolve(reference, { signal } = {}) {
            if (!TYPES.includes(reference.kind)) return null
            const snapshot = reference.snapshot
            const ids = snapshot?.ids || {}
            const imdb = imdbId(reference.source === 'imdb' ? reference.id : ids.imdb)
            let media = null
            if (imdb) {
                try {
                    media = await provider.getDetails(`${reference.kind}:${imdb}`, { signal })
                } catch (error) {
                    if (signal?.aborted || !tmdbId(ids.tmdb)) throw error
                }
            }
            else if (reference.source === 'provider' && reference.provider === 'vidrift') {
                media = await provider.getDetails(reference.id, { signal })
            }
            if (media) {
                if (reference.source === 'tmdb' && String(media.ids.tmdb) !== reference.id) return null
                if (ids.tmdb && media.ids.tmdb && String(ids.tmdb) !== String(media.ids.tmdb)) return null
                return withSnapshot(media, snapshot)
            }
            // A saved TMDB identity can still play without a metadata lookup.
            if (snapshot && tmdbId(ids.tmdb)) {
                return {
                    ...snapshot, provider: 'vidrift', id: `${reference.kind}:tmdb:${ids.tmdb}`,
                    images: { posters: [], backdrops: [], stills: [] },
                    ids: { kinopoisk: null, imdb: null, shikimori: null, ...ids },
                }
            }
            if (reference.source === 'kinopoisk' || (reference.source === 'provider' && reference.provider === 'kinobd')) {
                const error = new Error('No portable identity in saved KinoBD record')
                error.code = 'MEDIA_ID_UNAVAILABLE'
                throw error
            }
            return null
        },
        getPlayer: vidriftPlayer,
    }
    return provider
}

export const vidriftProvider = createVidriftProvider()
